import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Centralized logging service
 * Filters console output in production builds
 * Provides consistent logging format across the application
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly isProduction = environment.production;

  /**
   * Log informational messages (only in development)
   */
  log(...args: any[]): void {
    if (!this.isProduction) {
      console.log(...args);
    }
  }

  /**
   * Log warnings (only in development)
   */
  warn(...args: any[]): void {
    if (!this.isProduction) {
      console.warn(...args);
    }
  }

  /**
   * Log errors (always logged, but formatted consistently)
   */
  error(context: string, error: unknown, ...additionalArgs: any[]): void {
    const prefix = `[${context}]`;
    
    if (error instanceof Error) {
      console.error(prefix, error.message, error.stack, ...additionalArgs);
    } else if (typeof error === 'object' && error !== null) {
      try {
        console.error(prefix, JSON.stringify(error), ...additionalArgs);
      } catch {
        console.error(prefix, String(error), ...additionalArgs);
      }
    } else {
      console.error(prefix, String(error), ...additionalArgs);
    }
  }

  /**
   * Log debug messages (only in development)
   */
  debug(...args: any[]): void {
    if (!this.isProduction) {
      console.debug(...args);
    }
  }

  /**
   * Log with custom level
   */
  logWithLevel(level: 'log' | 'warn' | 'error' | 'debug', ...args: any[]): void {
    switch (level) {
      case 'log':
        this.log(...args);
        break;
      case 'warn':
        this.warn(...args);
        break;
      case 'error':
        this.error('App', args[0], ...args.slice(1));
        break;
      case 'debug':
        this.debug(...args);
        break;
    }
  }
}
